import type {
  PlaceSortField,
  PlaceSortOrder,
  PlacesFormSort,
} from '../types/placesSearch.types';
import { PLACES_FORM_SORT_OPTIONS } from '../types/placesSearch.types';
import type { PlaceFilters, PlaceItem, PlaceTypeOption } from '../../../../types/place.types';
import { isPlaceBooleanTrue, normalizePlaceFilters } from '../../../../types/place.types';

export function normalizePlaceSearchResponse(raw: unknown): PlaceItem[] {
  if (Array.isArray(raw)) return raw as PlaceItem[];
  if (raw && typeof raw === 'object' && raw !== null) {
    if ('results' in raw && Array.isArray((raw as { results: unknown }).results)) {
      return (raw as { results: PlaceItem[] }).results;
    }
    if ('name' in raw) {
      return [raw as PlaceItem];
    }
  }
  return [];
}

export function radiusMetersFromDistanceField(value: string): number {
  if (!value?.trim()) return 5000;
  const n = Number(value);
  return Number.isFinite(n) && n > 0 ? n : 5000;
}

export function selectablePlaceTypeOptions(apiTypes: PlaceTypeOption[]): PlaceTypeOption[] {
  return apiTypes.filter(t => {
    const v = t.value.trim().toLowerCase();
    const lab = t.label.trim().toLowerCase();
    if (!v) return false;
    if (v === 'all') return false;
    if (lab === 'all' || lab === 'all types') return false;
    return true;
  });
}

export function resolvePlaceTypeLabel(
  placeTypeValue: string,
  options: { value: string; label: string }[]
): string {
  if (!placeTypeValue || placeTypeValue === 'all') return 'All types';
  const hit = options.find(o => o.value === placeTypeValue);
  return hit?.label ?? placeTypeValue;
}

export function parsePlaceFormSortValue(value: string): {
  field: PlaceSortField;
  order: PlaceSortOrder;
} {
  const colonIndex = value.lastIndexOf(':');
  const fieldRaw = colonIndex >= 0 ? value.slice(0, colonIndex) : value;
  const orderRaw = colonIndex >= 0 ? value.slice(colonIndex + 1) : 'asc';
  const validFields: PlaceSortField[] = ['rating', 'distance', 'popularity'];
  const validOrders: PlaceSortOrder[] = ['asc', 'desc'];

  let field: PlaceSortField = validFields.includes(fieldRaw as PlaceSortField)
    ? (fieldRaw as PlaceSortField)
    : 'rating';

  if (fieldRaw === 'best_rating') {
    field = 'rating';
  }

  return {
    field,
    order: validOrders.includes(orderRaw as PlaceSortOrder) ? (orderRaw as PlaceSortOrder) : 'asc',
  };
}

export function placeFormSortToOrderingParam(sort: PlacesFormSort): string {
  const { field, order } = parsePlaceFormSortValue(sort);
  if (field === 'rating') {
    return order === 'asc' ? '-rating' : 'rating';
  }
  return order === 'desc' ? `-${field}` : field;
}

export function applyPlaceBooleanFilters(places: PlaceItem[], filters: PlaceFilters): PlaceItem[] {
  const normalized = normalizePlaceFilters(filters);

  if (!normalized.petFriendly && !normalized.goodForChildren && !normalized.goodForGroup) {
    return places;
  }

  return places.filter(place => {
    if (normalized.petFriendly && !isPlaceBooleanTrue(place.pet_friendly)) {
      return false;
    }
    if (normalized.goodForChildren && !isPlaceBooleanTrue(place.good_for_children)) {
      return false;
    }
    if (normalized.goodForGroup && !isPlaceBooleanTrue(place.good_for_group)) {
      return false;
    }
    return true;
  });
}

export function resolveRankLabel(sort: PlacesFormSort): string {
  const hit = PLACES_FORM_SORT_OPTIONS.find(option => option.value === sort);
  return hit?.label ?? 'Best rating (high to low)';
}
